import { Component, DestroyRef, EventEmitter, HostListener, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
// Generated by GitHub Copilot - Oct-08-2026
import { RouterLink } from '@angular/router';
import { ReviewerService } from '../../core/services/reviewer.service';
import { MasterService } from '../../core/services/master.service';
import { RebarAuthService } from '../../core/rebarauth/rebar.auth.service';
import { UserRoleService } from '../../core/services/user-role.service';
import { PeoplePickerComponent, IPeopleDetails } from '../../core/components/people-picker/people-picker.component';
import { DateFormatService } from '../../core/services/date-format.service';

@Component({
  standalone: true,
  selector: 'app-create-new-request',
  imports: [MatIconModule, CommonModule, FormsModule, PeoplePickerComponent, RouterLink],
  templateUrl: './create-new-request.component.html',
  styleUrl: './create-new-request.component.css',
})
export class CreateNewRequestComponent {
  private destroyRef      = inject(DestroyRef);
  private _activePicker: HTMLInputElement | null = null;
  private _pickerResizeHandler: (() => void) | null = null;
  private reviewerService = inject(ReviewerService);
  private masterService   = inject(MasterService);
  private rebarAuth       = inject(RebarAuthService);
  private userRoleService = inject(UserRoleService);
  private dateFormatSvc   = inject(DateFormatService);

  formatDateDisplay(value: string | null | undefined): string {
    return this.dateFormatSvc.format(value);
  }

  openDatePicker(input: HTMLInputElement): void {
    if (!input.disabled) {
      this._activePicker = input;
      if (!this._pickerResizeHandler) {
        this._pickerResizeHandler = () => {
          if (this._activePicker && !this._activePicker.disabled) {
            const picker = this._activePicker;
            picker.blur();
            setTimeout(() => picker.showPicker?.(), 0);
          }
        };
        window.addEventListener('resize', this._pickerResizeHandler);
        this.destroyRef.onDestroy(() => window.removeEventListener('resize', this._pickerResizeHandler!));
      }
      input?.showPicker?.();
    }
  }

  @Input()  existingSlNames: string[] = [];
  @Output() close     = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<string>(); // emits requestType on success

  selectedRequestType = '';
  showTypeDropdown    = false;
  isSubmitting        = false;
  submitError         = '';

  get requestTypeOptions(): string[] {
    const role = this.userRoleService.rollName?.toLowerCase() ?? '';
    if (role === 'reviewer' || role === 'manager' || role === 'supervisor' || role === 'administrator') {
      return [
        'Add New User',
        'Accenture Operation Service Line',
        'Request Not Applicable Plan Review Activity/Stage',
      ];
    }
    if (role === 'r&c member') {
      return [
        'Add New User',
        'Accenture Operation Service Line',
      ];
    }
    return [];
  }

  // ── AOSL form fields ──
  aoslName                = '';
  aoslContractNumber      = '';
  aoslMasterCustomerNumber = '';
  aoslMarket              = '';
  aoslMarketUnit          = '';
  aoslComments            = '';
  aoslAttachedFiles: File[]    = [];
  aoslAttachedFileNames: string[] = [];
  attachError = '';
  isDuplicateName         = false;
  showErrors              = false;
  showMarketDropdown      = false;
  showMarketUnitDropdown  = false;
  marketOptions: string[] = [];
  marketUnitOptions: string[] = [];
  private repLetterProjects: any[] = [];

  // ── Add New User form fields ──
  addUserEmail        = '';
  addUserFirstName    = '';
  addUserLastName     = '';
  addUserDesignation  = '';
  addUserCountry      = '';
  addUserCountryId: number | null = null;
  addUserWorkLocation = '';
  addUserExpirationOn = '';
  showAddUserErrors   = false;
  addUserEmailError    = '';
  addUserEidAutoFilled = false;
  private validEidMap  = new Map<string, { firstName: string; lastName: string }>();

  showDesignationDropdown  = false;
  showCountryDropdown      = false;
  showWorkLocationDropdown = false;
  countrySearchTerm        = '';

  get filteredCountries(): { id: number; name: string }[] {
    const term = this.countrySearchTerm.toLowerCase().trim();
    if (!term) return this.countryList;
    return this.countryList.filter(c => c.name.toLowerCase().includes(term));
  }

  countryList: { id: number; name: string }[] = [];
  workLocationList: string[] = [];
  isLoadingCities     = false;
  isLoadingCountries  = false;
  isLoadingMarkets    = false;

  designationOptions: string[] = [];

  // ── Not Applicable form fields ──
  naSelectedReviewId: number | null = null;
  naSelectedReviewLabel = '';
  naReason = '';
  naAttachedFiles: File[] = [];
  naAttachedFileNames: string[] = [];
  naAttachError = '';
  showNaErrors = false;
  showNaReviewDropdown = false;
  naReviewList: { id: number; label: string }[] = [];
  isLoadingNaReviews = false;
  naIsDuplicate = false;
  naIsDuplicateChecking = false;

  // ── Revised Target Publication Date form fields ──
  rtpdSelectedReviewId: number | null = null;
  rtpdSelectedReviewLabel = '';
  rtpdNewTargetDate = '';
  rtpdReason = '';
  showRtpdErrors = false;
  showRtpdReviewDropdown = false;
  rtpdReviewList: { id: number; label: string }[] = [];
  isLoadingRtpdReviews = false;
  rtpdIsDuplicate = false;
  rtpdIsDuplicateChecking = false;

  get todayDateStr(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  get isAoslFormDirty(): boolean {
    return !!(
      this.aoslName.trim() || this.aoslContractNumber.trim() || this.aoslMasterCustomerNumber.trim() ||
      this.aoslMarket || this.aoslMarketUnit || this.aoslComments.trim() || this.aoslAttachedFiles.length
    );
  }

  get canSubmit(): boolean {
    return !!(this.aoslName.trim() && this.aoslComments.trim()) && !this.isDuplicateName;
  }

  get isAddUserFormDirty(): boolean {
    return !!(
      this.addUserEmail.trim() || this.addUserFirstName.trim() || this.addUserLastName.trim() ||
      this.addUserDesignation || this.addUserCountry || this.addUserWorkLocation || this.addUserExpirationOn
    );
  }

  get canSubmitAddUser(): boolean {
    return !!(
      this.addUserEmail.trim() && this.addUserFirstName.trim() && this.addUserLastName.trim() &&
      this.addUserDesignation && this.addUserCountry && this.addUserWorkLocation
    ) && !this.addUserEmailError;
  }

  get isNaFormDirty(): boolean {
    return !!(this.naSelectedReviewId || this.naReason.trim() || this.naAttachedFiles.length);
  }

  get canSubmitNa(): boolean {
    return !!(this.naSelectedReviewId && this.naReason.trim()) && !this.naIsDuplicate;
  }

  get isRtpdFormDirty(): boolean {
    return !!(this.rtpdSelectedReviewId || this.rtpdNewTargetDate || this.rtpdReason.trim());
  }

  get canSubmitRtpd(): boolean {
    return !!(this.rtpdSelectedReviewId && this.rtpdNewTargetDate && this.rtpdReason.trim()) && !this.rtpdIsDuplicate;
  }

  checkDuplicate() {
    const name = this.aoslName.trim().toLowerCase();
    this.isDuplicateName = !!(name && this.existingSlNames.some(n => n.trim().toLowerCase() === name));
  }

  // ── Type dropdown ──
  toggleTypeDropdown() {
    this.showTypeDropdown = !this.showTypeDropdown;
    this.showMarketDropdown = false;
    this.showMarketUnitDropdown = false;
  }

  selectRequestType(opt: string) {
    this.selectedRequestType = opt;
    this.showTypeDropdown = false;
    this.resetFormFields();
    if (opt === 'Add New User') {
      this.loadCountries();
      this.loadDesignations();
      this.loadValidEids();
    }
    if (opt === 'Accenture Operation Service Line') {
      this.loadRepLetterProjects();
    } else if (opt === 'Request Not Applicable Plan Review Activity/Stage') {
      this.loadNaReviews();
    } //else if (opt === 'Revised Target Publication Date') {
    //   this.loadRtpdReviews();
    // }
  }

  private loadCountries(): void {
    this.isLoadingCountries = true;
    this.reviewerService.getAllCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.countryList = (res || [])
            .map(c => ({ id: c.CountryMasterID ?? c.ID ?? c.id, name: c.CountryName ?? c.Name ?? c.name ?? '' }))
            .filter(c => c.name);
          this.isLoadingCountries = false;
        },
        error: () => { this.isLoadingCountries = false; },
      });
  }

  private loadRepLetterProjects(): void {
    this.isLoadingMarkets = true;
    this.reviewerService.getProjectDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.repLetterProjects = res || [];
          this.marketOptions = [...new Set(this.repLetterProjects.map(p => p.Market).filter(Boolean))];
          this.isLoadingMarkets = false;
        },
        error: () => { this.repLetterProjects = []; this.marketOptions = []; this.isLoadingMarkets = false; },
      });
  }

  private loadDesignations(): void {
    this.masterService.getRoleNames()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.designationOptions = (res || [])
            .map(r => typeof r === 'string' ? r : (r.RoleName ?? r.Role_Name ?? r.roleName ?? r.Name ?? r.name ?? ''))
            .filter(Boolean);
        },
        error: () => { this.designationOptions = []; },
      });
  }

  // ── AOSL dropdowns ──
  toggleMarketDropdown() {
    this.showMarketDropdown = !this.showMarketDropdown;
    this.showMarketUnitDropdown = false;
    this.showTypeDropdown = false;
  }

  selectMarket(opt: string) {
    this.aoslMarket = opt;
    this.aoslMarketUnit = '';
    this.marketUnitOptions = [...new Set(
      this.repLetterProjects
        .filter(p => p.Market === opt)
        .map(p => p.MarketUnit)
        .filter(Boolean)
    )];
    this.showMarketDropdown = false;
  }

  toggleMarketUnitDropdown() {
    if (!this.aoslMarket) return;
    this.showMarketUnitDropdown = !this.showMarketUnitDropdown;
    this.showMarketDropdown = false;
    this.showTypeDropdown = false;
  }

  selectMarketUnit(opt: string) {
    this.aoslMarketUnit = opt;
    this.showMarketUnitDropdown = false;
  }

  onPersonSelected(person: IPeopleDetails): void {
    this.addUserFirstName     = person.FirstName ?? '';
    this.addUserLastName      = person.LastName  ?? '';
    this.addUserEmailError    = '';
    this.addUserEidAutoFilled = true;
  }

  private loadValidEids(): void {
    this.validEidMap.clear();
    this.reviewerService.getUsersInformation('').subscribe({
      next: (res: any) => {
        const list: any[] = Array.isArray(res) ? res : (res?.value ?? res?.recordset ?? []);
        list.forEach((u: any) => {
          const raw = u.EnterpriseId ?? u.enterpriseId ?? u.EID ?? u.eid ?? u.Email ?? u.email ?? '';
          const eid = (raw.includes('@') ? raw.split('@')[0] : raw).toLowerCase().trim();
          if (eid) this.validEidMap.set(eid, {
            firstName: u.FirstName ?? u.firstName ?? '',
            lastName:  u.LastName  ?? u.lastName  ?? '',
          });
        });
      },
      error: () => {},
    });
  }

  onAddUserEmailInput(): void {
    this.addUserEmailError    = '';
    this.addUserEidAutoFilled = false;
    this.addUserFirstName     = '';
    this.addUserLastName      = '';
  }

  // validateAddUserEid(): void {
  //   const raw = this.addUserEmail.trim();
  //   if (!raw || this.validEidMap.size === 0) return;
  //   const eid = (raw.includes('@') ? raw.split('@')[0] : raw).toLowerCase();
  //   if (this.validEidMap.has(eid)) {
  //     this.addUserEmailError    = 'Users already active in AST.';
  //     this.addUserEidAutoFilled = false;
  //   } else {
  //     this.addUserEmailError = '';
  //   }
  // }

  validateAddUserEid(): void {
    if (this.addUserEidAutoFilled) return;
    const raw = this.addUserEmail.trim();
    if (!raw) return;
    this.addUserEmailError = 'Entered EID is not valid. Please enter a valid EID.';
  }

  // ── Add New User dropdowns ──
  toggleDesignationDropdown(): void {
    this.showDesignationDropdown = !this.showDesignationDropdown;
    this.showCountryDropdown = false; this.showWorkLocationDropdown = false;
  }

  selectDesignation(opt: string): void { this.addUserDesignation = opt; this.showDesignationDropdown = false; }

  toggleCountryDropdown(): void {
    this.showCountryDropdown = !this.showCountryDropdown;
    if (this.showCountryDropdown) this.countrySearchTerm = '';
    this.showDesignationDropdown = false; this.showWorkLocationDropdown = false;
  }

  selectCountry(c: { id: number; name: string }): void {
    this.addUserCountry = c.name;
    this.addUserCountryId = c.id;
    this.addUserWorkLocation = '';
    this.workLocationList = [];
    this.showCountryDropdown = false;
    this.countrySearchTerm = '';
    this.isLoadingCities = true;
    this.reviewerService.getAllCities(c.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.workLocationList = (res || []).map(ci => ci.CityName ?? ci.Name ?? ci.name ?? '').filter(Boolean);
          this.isLoadingCities = false;
        },
        error: () => {
          this.workLocationList = [];
          this.isLoadingCities = false;
        },
      });
  }

  toggleWorkLocationDropdown(): void {
    if (!this.addUserCountry) return;
    this.showWorkLocationDropdown = !this.showWorkLocationDropdown;
    this.showDesignationDropdown = false; this.showCountryDropdown = false;
  }

  selectWorkLocation(loc: string): void { this.addUserWorkLocation = loc; this.showWorkLocationDropdown = false; }

  // ── Not Applicable: reviews ──
  // 
  
  private loadNaReviews(): void {
    this.isLoadingNaReviews = true;
    this.masterService.getReviewID()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {

          this.naReviewList = (res || []).map(item => ({
            id: item.ReviewID,
            label: item.ReviewID
          }));

          this.isLoadingNaReviews = false;
        },
        error: () => {
          this.isLoadingNaReviews = false;
        }
      });
  }

  toggleNaReviewDropdown(): void {
    this.showNaReviewDropdown = !this.showNaReviewDropdown;
    this.showTypeDropdown = false;
  }

  selectNaReview(r: { id: number; label: string }): void {
    this.naSelectedReviewId = r.id;
    this.naSelectedReviewLabel = r.label;
    this.showNaReviewDropdown = false;
    this.naIsDuplicate = false;
    this.checkNaDuplicate(r.id);
  }

  private checkNaDuplicate(reviewId: number): void {
    this.naIsDuplicateChecking = true;
    this.masterService.getRequests()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          const requests = res || [];
          this.naIsDuplicate = requests.some(req =>
            req.ReviewID == reviewId &&
            req.RequestType === 'Request Not Applicable Plan Review Activity/Stage' &&
            req.Status !== 'Rejected' && req.Status !== 'Cancelled'
          );
          this.naIsDuplicateChecking = false;
        },
        error: () => { this.naIsDuplicateChecking = false; }
      });
  }

  onNaFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const MAX_SIZE = 20 * 1024 * 1024;
      const valid: File[] = [];
      const rejected: string[] = [];
      Array.from(input.files).forEach(f => {
        if (f.size > MAX_SIZE) { rejected.push(f.name); } else { valid.push(f); }
      });
      this.naAttachError = rejected.length ? `File(s) exceed 20MB limit: ${rejected.join(', ')}` : '';
      if (valid.length) {
        this.naAttachedFiles = [...this.naAttachedFiles, ...valid];
        this.naAttachedFileNames = this.naAttachedFiles.map(f => f.name);
      }
      input.value = '';
    }
  }

  removeNaAttachedFile(index: number): void {
    this.naAttachedFiles = this.naAttachedFiles.filter((_, i) => i !== index);
    this.naAttachedFileNames = this.naAttachedFileNames.filter((_, i) => i !== index);
    if (!this.naAttachedFiles.length) this.naAttachError = '';
  }

  // ── Revised Target Publication Date: reviews ──
  private loadRtpdReviews(): void {
    this.isLoadingRtpdReviews = true;
    this.masterService.getReviewID()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.rtpdReviewList = (res || []).map(item => ({
            id: item.ReviewID,
            label: item.ReviewName || item.ReviewID
          }));
          this.isLoadingRtpdReviews = false;
        },
        error: () => { this.isLoadingRtpdReviews = false; }
      });
  }

  toggleRtpdReviewDropdown(): void {
    this.showRtpdReviewDropdown = !this.showRtpdReviewDropdown;
    this.showTypeDropdown = false;
  }

  selectRtpdReview(r: { id: number; label: string }): void {
    this.rtpdSelectedReviewId = r.id;
    this.rtpdSelectedReviewLabel = r.label;
    this.showRtpdReviewDropdown = false;
    this.rtpdIsDuplicate = false;
    this.checkRtpdDuplicate(r.id);
  }

  private checkRtpdDuplicate(reviewId: number): void {
    this.rtpdIsDuplicateChecking = true;
    this.masterService.getRequests()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          const requests = res || [];
          this.rtpdIsDuplicate = requests.some(req =>
            req.ReviewID == reviewId &&
            req.RequestType === 'Revised Target Publication Date' &&
            req.Status !== 'Rejected' && req.Status !== 'Cancelled'
          );
          this.rtpdIsDuplicateChecking = false;
        },
        error: () => { this.rtpdIsDuplicateChecking = false; }
      });
  }

  // ── File handling ──
  onlyNumbers(event: KeyboardEvent) {
    if (!/[0-9]/.test(event.key)) event.preventDefault();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const MAX_SIZE = 20 * 1024 * 1024;
      const valid: File[] = [];
      const rejected: string[] = [];
      Array.from(input.files).forEach(f => {
        if (f.size > MAX_SIZE) { rejected.push(f.name); } else { valid.push(f); }
      });
      this.attachError = rejected.length
        ? `File(s) exceed 20MB limit: ${rejected.join(', ')}`
        : '';
      if (valid.length) {
        this.aoslAttachedFiles = [...this.aoslAttachedFiles, ...valid];
        this.aoslAttachedFileNames = this.aoslAttachedFiles.map(f => f.name);
      }
      input.value = '';
    }
  }

  removeAttachedFile(index: number): void {
    this.aoslAttachedFiles = this.aoslAttachedFiles.filter((_, i) => i !== index);
    this.aoslAttachedFileNames = this.aoslAttachedFileNames.filter((_, i) => i !== index);
    if (!this.aoslAttachedFiles.length) this.attachError = '';
  }

  cancelForm() { this.close.emit(); }

  // ── AOSL submit ──
  async saveForm(): Promise<void> {
    this.showErrors = true;
    if (!this.canSubmit || this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    const user = this.rebarAuth.getUserName() ?? '';
    const documentsJson = await this.buildDocumentsJson(this.aoslAttachedFiles, user);
    const params = {
      category:                    'InsertNewRequestTesting',
      RequestType:                 this.selectedRequestType,
      Status:                      'New',
      Title:                       null,
      SubTitle:                    null,
      ReviewID:                    null,
      AssignedTo:                  null,
      EmailEnterpriseID:           null,
      FirstName:                   null,
      LastName:                    null,
      RCFunctionDesignation:       null,
      Country:                     null,
      WorkLocation:                null,
      ExpirationOn:                null,
      OperationRequestType:        null,
      OperationServiceLine:        this.aoslName.trim(),
      ContractNumber:              this.aoslContractNumber || null,
      MasterCustomerNumber:        this.aoslMasterCustomerNumber || null,
      Market:                      this.aoslMarket || null,
      MarketUnit:                  this.aoslMarketUnit || null,
      CancellationReason:          null,
      NotApplicableReason:         null,
      RevisedTargetPublicationDate: null,
      RevisedDateReason:           null,
      AcknowledgementText:         null,
      AcknowledgementAccepted:     null,
      Comments:                    this.aoslComments.trim() || null,
      DocumentsJSON:               documentsJson,
      RequestedBy:                 user,
      CreatedBy:                   user,
    };
    this.masterService.insertNewRequest(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.submitted.emit(this.selectedRequestType);
        },
        error: () => {
          this.isSubmitting = false;
          this.submitError = 'Failed to submit request. Please try again.';
        },
      });
  }

  // ── Add New User submit ──
  async saveAddUserForm(): Promise<void> {
    this.showAddUserErrors = true;
    this.validateAddUserEid();
    if (!this.canSubmitAddUser || this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    this.addUserEmailError = '';
    const user = this.rebarAuth.getUserName() ?? '';
    const params = {
      category:                    'InsertNewRequestTesting',
      RequestType:                 this.selectedRequestType,
      Status:                      'New',
      Title:                       null,
      SubTitle:                    null,
      ReviewID:                    null,
      AssignedTo:                  null,
      EmailEnterpriseID:           this.addUserEmail.trim(),
      FirstName:                   this.addUserFirstName.trim(),
      LastName:                    this.addUserLastName.trim(),
      RCFunctionDesignation:       this.addUserDesignation,
      Country:                     this.addUserCountry,
      WorkLocation:                this.addUserWorkLocation,
      ExpirationOn:                this.addUserExpirationOn || null,
      OperationRequestType:        null,
      OperationServiceLine:        null,
      ContractNumber:              null,
      MasterCustomerNumber:        null,
      Market:                      null,
      MarketUnit:                  null,
      CancellationReason:          null,
      NotApplicableReason:         null,
      RevisedTargetPublicationDate: null,
      RevisedDateReason:           null,
      AcknowledgementText:         null,
      AcknowledgementAccepted:     null,
      Comments:                    null,
      DocumentsJSON:               null,
      RequestedBy:                 user,
      CreatedBy:                   user,
    };
    this.masterService.insertNewRequest(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.isSubmitting = false;
          const items = Array.isArray(res) ? res : (res ? [res] : []);
          const apiMessage = items[0]?.Message ?? '';
          if (apiMessage === 'Users already active in AST.') {
            this.addUserEmailError = apiMessage;
            return;
          }
          this.submitted.emit(this.selectedRequestType);
        },
        error: () => {
          this.isSubmitting = false;
          this.submitError = 'Failed to submit request. Please try again.';
        },
      });
  }

  // ── Not Applicable submit ──
  async saveNaForm(): Promise<void> {
    this.showNaErrors = true;
    if (!this.canSubmitNa || this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    const user = this.rebarAuth.getUserName() ?? '';
    const documentsJson = await this.buildDocumentsJson(this.naAttachedFiles, user);
    const params = {
      category:                    'InsertNewRequestTesting',
      RequestType:                 this.selectedRequestType,
      Status:                      'New',
      Title:                       null,
      SubTitle:                    null,
      ReviewID:                    this.naSelectedReviewId,
      AssignedTo:                  null,
      EmailEnterpriseID:           null,
      FirstName:                   null,
      LastName:                    null,
      RCFunctionDesignation:       null,
      Country:                     null,
      WorkLocation:                null,
      ExpirationOn:                null,
      OperationRequestType:        null,
      OperationServiceLine:        null,
      ContractNumber:              null,
      MasterCustomerNumber:        null,
      Market:                      null,
      MarketUnit:                  null,
      CancellationReason:          null,
      NotApplicableReason:         this.naReason.trim(),
      RevisedTargetPublicationDate: null,
      RevisedDateReason:           null,
      AcknowledgementText:         null,
      AcknowledgementAccepted:     null,
      Comments:                    null,
      DocumentsJSON:               documentsJson,
      RequestedBy:                 user,
      CreatedBy:                   user,
    };
    this.masterService.insertNewRequest(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.submitted.emit(this.selectedRequestType);
        },
        error: () => {
          this.isSubmitting = false;
          this.submitError = 'Failed to submit request. Please try again.';
        },
      });
  }

  // ── Revised Target Publication Date submit ──
  async saveRtpdForm(): Promise<void> {
    this.showRtpdErrors = true;
    if (!this.canSubmitRtpd || this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    const user = this.rebarAuth.getUserName() ?? '';
    const params = {
      category:                    'InsertNewRequestTesting',
      RequestType:                 this.selectedRequestType,
      Status:                      'New',
      Title:                       null,
      SubTitle:                    null,
      ReviewID:                    this.rtpdSelectedReviewId,
      AssignedTo:                  null,
      EmailEnterpriseID:           null,
      FirstName:                   null,
      LastName:                    null,
      RCFunctionDesignation:       null,
      Country:                     null,
      WorkLocation:                null,
      ExpirationOn:                null,
      OperationRequestType:        null,
      OperationServiceLine:        null,
      ContractNumber:              null,
      MasterCustomerNumber:        null,
      Market:                      null,
      MarketUnit:                  null,
      CancellationReason:          null,
      NotApplicableReason:         null,
      RevisedTargetPublicationDate: this.rtpdNewTargetDate,
      RevisedDateReason:           this.rtpdReason.trim(),
      AcknowledgementText:         null,
      AcknowledgementAccepted:     null,
      Comments:                    null,
      DocumentsJSON:               null,
      RequestedBy:                 user,
      CreatedBy:                   user,
    };
    this.masterService.insertNewRequest(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.isSubmitting = false;
          const items = Array.isArray(res) ? res : (res ? [res] : []);
          const apiMessage = items[0]?.Message ?? '';
          if (apiMessage === 'Users already active in AST.') {
            this.addUserEmailError = apiMessage;
            return;
          }
          this.submitted.emit(this.selectedRequestType);
        },
        error: () => {
          this.isSubmitting = false;
          this.submitError = 'Failed to submit request. Please try again.';
        },
      });
  }

  private async buildDocumentsJson(files: File[], uploadedBy: string): Promise<string | null> {
    if (!files.length) return null;
    const docs = await Promise.all(files.map(async (file) => {
      const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
      const fileContent = await this.readFileAsBase64(file);
      return {
        DocumentID:      null,
        DocumentType:    ext,
        UploadedBy:      uploadedBy,
        UploadedDate:    new Date().toISOString(),
        IsActive:        true,
        Description:     '',
        FileName:        file.name,
        StorageFilePath: `R-1/${file.name}`,
        fileContent,
        FileSize:        file.size,
        FileType:        ext,
      };
    }));
    return JSON.stringify(docs);
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.substring(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private resetFormFields() {
    this.aoslName = ''; this.aoslContractNumber = ''; this.aoslMasterCustomerNumber = '';
    this.aoslMarket = ''; this.aoslMarketUnit = ''; this.aoslComments = '';
    this.aoslAttachedFiles = []; this.aoslAttachedFileNames = []; this.attachError = '';
    this.marketOptions = []; this.marketUnitOptions = []; this.repLetterProjects = []; this.isLoadingMarkets = false;
    this.isDuplicateName = false; this.showErrors = false;
    this.showMarketDropdown = false; this.showMarketUnitDropdown = false;
    this.addUserEmail = ''; this.addUserFirstName = ''; this.addUserLastName = '';
    this.addUserDesignation = ''; this.addUserCountry = ''; this.addUserCountryId = null;
    this.addUserWorkLocation = ''; this.addUserExpirationOn = '';
    this.showAddUserErrors = false; this.addUserEmailError = ''; this.addUserEidAutoFilled = false;
    this.showDesignationDropdown = false; this.showCountryDropdown = false;
    this.showWorkLocationDropdown = false; this.countrySearchTerm = '';
    this.workLocationList = []; this.countryList = [];
    this.isLoadingCities = false; this.isLoadingCountries = false;
    this.isSubmitting = false; this.submitError = '';
    this.naSelectedReviewId = null; this.naSelectedReviewLabel = ''; this.naReason = '';
    this.naAttachedFiles = []; this.naAttachedFileNames = []; this.naAttachError = '';
    this.showNaErrors = false; this.showNaReviewDropdown = false;
    this.naReviewList = []; this.isLoadingNaReviews = false;
    this.naIsDuplicate = false; this.naIsDuplicateChecking = false;
    this.rtpdSelectedReviewId = null; this.rtpdSelectedReviewLabel = ''; this.rtpdNewTargetDate = '';
    this.rtpdReason = ''; this.showRtpdErrors = false; this.showRtpdReviewDropdown = false;
    this.rtpdReviewList = []; this.isLoadingRtpdReviews = false;
    this.rtpdIsDuplicate = false; this.rtpdIsDuplicateChecking = false;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: any) {
    if (!event.target.closest('.type-select-trigger'))       this.showTypeDropdown = false;
    if (!event.target.closest('.market-select-trigger'))     this.showMarketDropdown = false;
    if (!event.target.closest('.market-unit-select-trigger')) this.showMarketUnitDropdown = false;
    if (!event.target.closest('.add-user-designation-trigger'))   this.showDesignationDropdown = false;
    if (!event.target.closest('.add-user-country-trigger'))       this.showCountryDropdown = false;
    if (!event.target.closest('.add-user-location-trigger'))      this.showWorkLocationDropdown = false;
    if (!event.target.closest('.na-review-select-trigger'))   this.showNaReviewDropdown = false;
    if (!event.target.closest('.rtpd-review-select-trigger')) this.showRtpdReviewDropdown = false;
  }
}
